import { CaseStatus } from '@debbie/common/dist/cases/index.js'
import {
  EmbedPlacement,
  getEmbedAreas,
} from '@debbie/common/dist/embeds/index.js'
import { AggregationType } from '@debbie/common/dist/insights/aggregations.js'
import { eq } from '@debbie/common/dist/query/index.js'
import { TenantStatus, TenantType } from '@debbie/common/dist/tenants/index.js'
import { cappedText } from '@debbie/common/dist/util/text.js'
import { usePausedCases } from '@debbie/cortex/dist/home/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useCaseFilter } from '@debbie/cortex/dist/insights/caseFilter.js'
import { useAggregation } from '@debbie/cortex/dist/insights/index.js'
import { useInteractionInsightsTotals } from '@debbie/cortex/dist/interactions/index.js'
import { hasData } from '@debbie/cortex/dist/Loadable/helpers.js'
import { useMeTenant, useRole } from '@debbie/cortex/dist/me/index.js'
import { useMe } from '@debbie/cortex/dist/me/index.js'
import { useProcedures } from '@debbie/cortex/dist/procedures/index.js'
import { useQuery } from '@debbie/cortex/dist/query/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useTemplates } from '@debbie/cortex/dist/templates/index.js'
import { useTenant } from '@debbie/cortex/dist/tenant/index.js'
import { useUpdateTypes } from '@debbie/cortex/dist/update-types/index.js'
import { useWorkflows } from '@debbie/cortex/dist/workflows/index.js'
import Archive from '@debbie/iris/dist/components/Icons/Archive.js'
import BarChart2 from '@debbie/iris/dist/components/Icons/BarChart2.js'
import BatteryCharging from '@debbie/iris/dist/components/Icons/BatteryCharging.js'
import Bookmark from '@debbie/iris/dist/components/Icons/Bookmark.js'
import Briefcase from '@debbie/iris/dist/components/Icons/Briefcase.js'
import Database from '@debbie/iris/dist/components/Icons/Database.js'
import HomeIcon from '@debbie/iris/dist/components/Icons/Home.js'
import { Monitor } from '@debbie/iris/dist/components/Icons/index.js'
import Mail from '@debbie/iris/dist/components/Icons/Mail.js'
import Phone from '@debbie/iris/dist/components/Icons/Phone.js'
import Send from '@debbie/iris/dist/components/Icons/Send.js'
import TrendingUp from '@debbie/iris/dist/components/Icons/TrendingUp.js'
import Users from '@debbie/iris/dist/components/Icons/Users.js'
import {
  AsyncComponent,
  ContentRight,
  LeftNav,
  RightNav,
} from '@debbie/iris/dist/components/index.js'
import THEME from '@debbie/iris/dist/theme.js'
import { Intercom } from '@intercom/messenger-js-sdk'
import * as Sentry from '@sentry/browser'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link, Route, Switch } from 'react-router-dom'
import { styled } from 'styled-components'

import logo from './assets/images/logoneg.svg'
import RedirectTo from './components/RedirectTo.js'
import { Error } from './pages/Error.js'
import { Loader } from './pages/Loader.js'
import { IdViewPage } from './pages/views/IdViewPage.js'
import { ViewQueuePage } from './pages/views/ViewQueuePage.js'
import { ViewsPage } from './pages/views/ViewsPage.js'

const Journalizer = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/Journalizer.js').then((module) => module.default),
  Sentry.captureException,
)

const OutboundCall = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/OutboundCall.js').then((module) => module.default),
  Sentry.captureException,
)

const InteractionHistory = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/InteractionHistory.js').then((module) => module.default),
  Sentry.captureException,
)

const Outbox = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/Outbox.js').then(async (module) => module.default),
  Sentry.captureException,
)

// TEMPORARY: debug-only, not in the nav. Remove together with pages/DebugAgentProposals.tsx.
const DebugAgentProposals = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/DebugAgentProposals.js').then(
      async (module) => module.default,
    ),
  Sentry.captureException,
)

const Home = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/Home.js').then((module) => module.default),
  Sentry.captureException,
)

const Cases = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/cases/Cases.js').then((module) => module.default),
  Sentry.captureException,
)

const Case = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/cases/Case.js').then((module) => module.default),
  Sentry.captureException,
)

const Imports = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/imports/Imports.js').then((module) => module.default),
  Sentry.captureException,
)

const Import = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/imports/Import.js').then((module) => module.default),
  Sentry.captureException,
)

const Reports = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/Reports.js').then((module) => module.default),
  Sentry.captureException,
)

const Creditors = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/creditors/Creditors.js').then((module) => module.default),
  Sentry.captureException,
)

const EmbedsPage = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/EmbedsPage.js').then((module) => module.default),
  Sentry.captureException,
)

const DesignSystem = AsyncComponent(
  Loader,
  Error,
  () =>
    import('@debbie/iris/dist/components/DesignSystem/index.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const Lighthouse = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/lighthouse/Lighthouse.js').then((module) => module.default),
  Sentry.captureException,
)

const CreateTenant = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/lighthouse/CreateTenant.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const TodoQueue = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/TodoQueue.js').then((module) => module.default),
  Sentry.captureException,
)

const CreateCreditor = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/creditors/creditor/create/CreateCreditor.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const Creditor = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/creditors/creditor/Creditor.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const Settings = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/settings/Settings.js').then((module) => module.default),
  Sentry.captureException,
)

const User = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/User.js').then((module) => module.default),
  Sentry.captureException,
)

const TenantBillingInvoice = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/TenantBillingInvoice.js').then((module) => module.default),
  Sentry.captureException,
)

const TenantBilling = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/lighthouse/TenantBilling.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const Developers = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/Developers.js').then((module) => module.default),
  Sentry.captureException,
)

const Economy = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/economy/Economy.js').then((module) => module.default),
  Sentry.captureException,
)

const ReturnLetters = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/ReturnLetters.js').then((module) => module.default),
  Sentry.captureException,
)

const CreateCase = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/cases/create/CreateCase.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const WorkflowEditor = AsyncComponent(
  Loader,
  Error,
  () =>
    import('./pages/settings/WorkflowEditor.js').then(
      (module) => module.default,
    ),
  Sentry.captureException,
)

const EConomicMapper = AsyncComponent(
  Loader,
  Error,
  () => import('./pages/EConomicMapper.js').then((module) => module.default),
  Sentry.captureException,
)

const LoggedInView = () => {
  const me = useMe()
  const lang = useSelector((s: RootState) => s.lang)
  const leftNavOpen = useSelector((s: RootState) => s.leftNavOpen)
  const redirectTo = useSelector((s: RootState) => s.redirectTo)
  const rightNavOpen = useSelector((s: RootState) => s.rightNavOpen)

  const aggregationLoadable = useAggregation(AggregationType.ByCreditor, {
    caseStatuses: [CaseStatus.PENDING],
  })
  const aggregation = h.hasData(aggregationLoadable)
    ? aggregationLoadable.data
    : null

  const caseFilter = useCaseFilter()
  const pausedCasesLoadable = usePausedCases(caseFilter)
  const pausedCases = h.hasData(pausedCasesLoadable)
    ? pausedCasesLoadable.data
    : null
  const meTenant = useMeTenant()
  const tenant = useTenant()
  const meLoader = useMe()
  const role = useRole()
  const interactionTotalsData = useInteractionInsightsTotals()

  const favoritedViews = useQuery('favoritedViews')({
    base: 'favoritedViews',
    orderBy: 'viewId',
    limit: 10,
    offset: 0,
    properties: {
      views: {
        fields: ['id', 'name'],
        markUnique: true,
      },
    },
    filters: eq(
      'favoritedViews.userId',
      h.hasData(meLoader) ? meLoader.data.id : '',
    ),
  })

  // Prefetch commonly needed data to avoid waterfall loading in nested components
  useProcedures()
  useWorkflows()
  useTemplates()
  useUpdateTypes()

  const interactionStats = h.hasData(interactionTotalsData)
    ? interactionTotalsData.data
    : null

  const setMobileView = (initial: boolean) => () => {
    const media = window.matchMedia(THEME.MEDIA_QUERY_MOBILE)
    if (media.matches && leftNavOpen) {
      c.local.toggleLeftNav()
    }
    if (!media.matches && !leftNavOpen && !initial) {
      c.local.toggleLeftNav()
    }
  }

  useEffect(() => {
    setMobileView(true)()
    window.addEventListener('resize', setMobileView(false))

    return () => {
      window.removeEventListener('resize', setMobileView(false))
    }
  }, [])

  useEffect(() => {
    if (meTenant) {
      const status =
        meTenant.status !== TenantStatus.Active
          ? `${lang.tenantStatus[meTenant.status]}: `
          : ''
      document.title = `${status}${meTenant.name} - Debbie`
    }
  }, [meTenant])

  useEffect(() => {
    if (hasData(me) && tenant && role === 'admin' && tenant.referenceId) {
      Intercom({
        app_id: 'jnivsz4e',
        region: 'eu',
        user_id: me.data.id,
        user_hash: me.data.intercomUserHash || undefined,
        email: me.data.email || undefined,
        name: me.data.firstName + ' ' + me.data.lastName,
        created_at: Math.floor(new Date(me.data.createdAt).getTime() / 1000),
        debbie_role: role,
        tenant_id: tenant.id,
        tenant_name: tenant.name,
        company: {
          id: tenant.referenceId,
          name: tenant.name,
        },
      })
    }
  }, [me, tenant, role])

  if (meTenant && meTenant.status === 'DISABLED') {
    return (
      <Blocked>
        <div className="content">
          <img src={logo} className="logo" alt={lang.misc.logo} width="100%" />
          <h1>{lang.auth.accountInactive}</h1>
          <p
            style={{
              fontSize: '0.8em',
            }}
          >
            {
              // @ts-ignore
              <Link to="/account-selector">
                {lang.auth.chooseBetweenAccounts}
              </Link>
            }
          </p>
        </div>
      </Blocked>
    )
  }

  const embedAreas = tenant
    ? getEmbedAreas(tenant.embeds, EmbedPlacement.CaseworkerWebMenuItem)
    : []

  const embedAreaLinks =
    embedAreas.length > 0
      ? {
          text: lang.embeds.caseworkerNavMenuItemCategory,
          exact: false,
          links: embedAreas.map((embedArea) => ({
            text: embedArea.title,
            path: `/embeds/${embedArea.embedId}`,
            icon: <Bookmark />,
          })),
        }
      : null

  return (
    <>
      <LeftNav
        logo={logo}
        links={[
          {
            text: lang.navigation.overview,
            path: '/',
            links: [
              {
                text: lang.navigation.home,
                icon: <HomeIcon />,
                path: '/',
                count:
                  (aggregation?.aggregation &&
                  aggregation.type === AggregationType.ByCreditor
                    ? aggregation.aggregation.reduce(
                        (acc: any, item: any) => item.count + acc,
                        0,
                      )
                    : 0) +
                  (pausedCases
                    ? pausedCases.reduce(
                        (acc: any, item: any) => item.count + acc,
                        0,
                      )
                    : 0),
              },
              {
                text: lang.navigation.cases,
                icon: <Briefcase />,
                path: '/cases',
              },
              {
                text: lang.navigation.views,
                icon: <Monitor />,
                exact: false,
                beta: true,
                path: '/views',
              },
            ],
          },
          {
            text: lang.navigation.economy,
            exact: false,
            path: '/economy/deposits',
            links: [
              {
                text: lang.navigation.economyDeposits,
                path: '/economy/deposits',
                icon: <TrendingUp />,
                exact: false,
              },
            ].concat(
              tenant
                ? tenant.type === TenantType.AccountsReceivable
                  ? {
                      text: lang.navigation.economyBookkeeping,
                      path: '/economy/bookkeeping',
                      icon: <BatteryCharging />,
                      exact: false,
                    }
                  : {
                      text: lang.navigation.economyBilling,
                      path: '/economy/billing',
                      icon: <BatteryCharging />,
                      exact: false,
                    }
                : [],
            ),
          },
          {
            text: lang.navigation.content,
            path:
              tenant?.type === TenantType.AccountsReceivable
                ? '/reports/downloads'
                : '/creditors',
            exact: false,
            links: (tenant?.type === TenantType.AccountsReceivable
              ? []
              : [
                  {
                    text: lang.navigation.creditors,
                    path: '/creditors',
                    icon: <Users />,
                  },
                ]
            ).concat([
              {
                text: lang.navigation.reports,
                path: '/reports/downloads',
                icon: <BarChart2 />,
              },
              {
                text: lang.navigation.import,
                path: '/imports',
                icon: <Database />,
              },
            ]),
          },
          {
            text: lang.navigation.communication,
            path: '/journalizer',
            links: [
              {
                text: <span>{lang.navigation.outboundCalls}</span>,
                icon: <Phone />,
                path: '/outbound',
              },
              {
                text: lang.navigation.journalizer,
                icon: <Archive />,
                path: '/journalizer',
                count: interactionStats ? interactionStats.journalizer : 0,
              },
              {
                text: lang.navigation.outbox,
                icon: <Send />,
                path: '/outbox',
                count: interactionStats ? interactionStats.outbox : 0,
              },
              {
                text: lang.navigation.interactionHistory,
                icon: <Mail />,
                path: '/interaction-history',
              },
            ].concat(
              tenant &&
                [
                  'd85b3ab3-dc9f-431f-b6dc-13cb71e0edc9', // Return letter tenant
                  '927712af-bb7b-4b6b-8e0a-3c323436df16', // Nuna Law tenant
                ].includes(tenant.id)
                ? [
                    {
                      text: lang.navigation.returnLetters,
                      icon: <Mail />,
                      path: '/return-letters',
                    },
                  ]
                : ([] as any),
            ),
          },
          ...(h.hasData(favoritedViews)
            ? favoritedViews.data.items.length != 0
              ? [
                  {
                    text: lang.navigation.views,
                    path: '/views',
                    links: favoritedViews.data.items.map((d) => {
                      return {
                        text:
                          d.views!.name === ''
                            ? lang.misc.unnamed
                            : cappedText(d.views!.name, 20),
                        icon: <Monitor />,
                        path: `/views/${d.views?.id}`,
                        exact: true,
                      }
                    }),
                  },
                ]
              : []
            : []),
        ].concat(embedAreaLinks ? embedAreaLinks : ([] as any))}
      />
      {rightNavOpen && <RightNav />}
      {redirectTo.to !== '' && <RedirectTo {...redirectTo} />}

      <Switch>
        <ContentRight rightNavOpen={rightNavOpen} leftNavOpen={leftNavOpen}>
          <Route exact path="/">
            <Home />
          </Route>
          <Route exact path="/journalizer">
            <Journalizer />
          </Route>
          <Route exact path="/outbound">
            <OutboundCall />
          </Route>
          <Route exact path="/outbound/:priority/:id">
            <OutboundCall />
          </Route>
          <Route exact path="/outbox">
            <Outbox />
          </Route>
          <Route exact path="/debug/agent-proposals">
            <DebugAgentProposals />
          </Route>
          <Route exact path="/interaction-history">
            <InteractionHistory />
          </Route>
          <Route exact path="/imports">
            <Imports />
          </Route>
          <Route path="/imports/:importId">
            <Import />
          </Route>
          <Route path="/reports">
            <Reports />
          </Route>
          <Route exact path="/creditors">
            <Creditors />
          </Route>
          <Route exact path="/create-creditor">
            <CreateCreditor />
          </Route>
          <Route path="/create-creditor/:creditorId/:state">
            <CreateCreditor />
          </Route>
          <Route path="/creditors/:id">
            <Creditor />
          </Route>
          <Route path="/settings">
            <Settings />
          </Route>
          <Route path="/user">
            <User />
          </Route>
          <Route path="/tenant-billing" exact>
            <TenantBilling />
          </Route>
          <Route path="/tenant-billing/:invoiceId">
            <TenantBillingInvoice />
          </Route>
          <Route path="/developers">
            <Developers />
          </Route>
          <Route exact path="/workflows/:workflowId">
            <WorkflowEditor />
          </Route>
          <Route path="/economy">
            <Economy />
          </Route>
          <Route exact path="/cases">
            <Cases />
          </Route>
          <Route exact path="/create-case">
            <CreateCase />
          </Route>
          <Route exact path="/create-case/:caseId/:slug">
            <CreateCase />
          </Route>
          <Route exact path="/cases/:ids">
            <Case />
          </Route>
          <Route exact path="/return-letters">
            <ReturnLetters />
          </Route>
          <Route exact path="/embeds/:id">
            <EmbedsPage />
          </Route>
          <Route exact path="/design-system">
            <DesignSystem />
          </Route>
          <Route exact path="/lighthouse">
            <Lighthouse />
          </Route>
          <Route exact path="/create-tenant/:status">
            <CreateTenant />
          </Route>
          <Route exact path="/todo-queue/:updateTypeId/:id">
            <TodoQueue />
          </Route>
          <Route exact path="/todo-queue/:updateTypeId">
            <TodoQueue />
          </Route>
          <Route exact path="/e-conomic-mapper/:creditorId">
            <EConomicMapper />
          </Route>
          <Route exact path="/views">
            <ViewsPage />
          </Route>
          <Route exact path="/views/:id">
            <IdViewPage />
          </Route>
          <Route exact path="/views/:id/queue/:itemId">
            <ViewQueuePage />
          </Route>
        </ContentRight>
      </Switch>
    </>
  )
}

export default LoggedInView

const Blocked = styled.div`
  position: fixed;
  height: 100vh;
  width: 100%;
  background-color: ${THEME.COLOR_SECONDARY};

  .content {
    text-align: center;
    width: 100%;
    max-width: 1200px;
    padding: 30px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);

    h1 {
      color: ${THEME.COLOR_ACCENT};
      font-size: 1.1em;
    }

    .logo {
      width: 250px;
      margin: 1em 0;
    }
  }
`
